import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { Button, ErrorState } from '@lipaflow/ui'
import { OWNER_NEXT_KEY, PORTAL_ROLE_KEY, ownerApi, recall, remember } from './api'

/**
 * The tenant portal's front door for someone who may also be an owner (rule 9.2). An owner only
 * goes to the owner portal; someone who is both chooses "As owner" or "As tenant" once a tab; a
 * decision link they had to sign in for opens on the decision (9.6).
 */
export default function RoleGate({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const me = useQuery({ queryKey: ['owner-me'], queryFn: ownerApi.me, retry: false, staleTime: 60_000 })
  if (me.isLoading) return <p role="status" className="p-6 text-sm text-stone-500">Loading…</p>
  if (me.isError) return <ErrorState title="Unable to load your portal roles" onRetry={() => { void me.refetch() }} />
  // A signed-in payer without an owner record continues as a tenant.
  if (!me.data?.is_owner) return <>{children}</>
  const next = recall(OWNER_NEXT_KEY)
  if (next && /^\/owner(?:\/|$)/.test(next)) {
    remember(OWNER_NEXT_KEY, null)
    remember(PORTAL_ROLE_KEY, 'owner')
    return <Navigate to={next} replace />
  }
  if (!me.data.is_tenant) return <Navigate to="/owner" replace />
  const role = recall(PORTAL_ROLE_KEY)
  if (role === 'owner') return <Navigate to="/owner" replace />
  if (role === 'tenant') return <>{children}</>
  const choose = (choice: 'owner' | 'tenant') => {
    remember(PORTAL_ROLE_KEY, choice)
    navigate(choice === 'owner' ? '/owner' : '/', { replace: true })
  }
  return <main className="mx-auto max-w-md p-6 space-y-4">
    <h1 className="font-outfit text-xl font-semibold">Welcome, {me.data.name}</h1>
    <p className="text-sm">You own property managed by {me.data.agency_name}, and you rent a home with them. How do you want to continue?</p>
    <div className="flex flex-col gap-3">
      <Button onClick={() => choose('owner')}>As owner</Button>
      <Button variant="secondary" onClick={() => choose('tenant')}>As tenant</Button>
    </div>
    <p className="text-xs text-stone-500">You can switch at any time. Your money as an owner and as a tenant is kept apart.</p>
  </main>
}
